import { BotBase } from './botBase';

import type {
	Command,
	GameState,
	Manager,
	PassCommand,
	PlayCommand,
} from '../lib';
import {
	assumeType,
	compareRank,
	GameAction,
	getCardObjectFromString,
	getRandomItem,
	getTrickWinner,
	makeNextStageCommand,
	Rank,
	RoundStage,
	Suit,
} from '../lib';

//This bot just plays the lowest possible card. It also tries to pass the Queen of Spades and aces

function getBetterPassCandidate(previousCandidate: PassCommand, nextCandidate: PassCommand): PassCommand {
	const previousHasQueen = previousCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.suit === Suit.Spades && card.rank === Rank.Queen;
	}).length > 0;

	const nextHasQueen = nextCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.suit === Suit.Spades && card.rank === Rank.Queen;
	}).length > 0;

	if (previousHasQueen && !nextHasQueen) {
		return previousCandidate;
	} else if (nextHasQueen && !previousHasQueen) {
		return nextCandidate;
	}

	const previousAceCount = previousCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.rank === Rank.Ace;
	}).length;

	const nextAceCount = nextCandidate.data.cards.filter((c) => {
		const card = getCardObjectFromString(c);
		return card.rank === Rank.Ace;
	}).length;

	if (previousAceCount > nextAceCount) {
		return previousCandidate;
	}

	return nextCandidate;
}

function getPassCommand(commands: Command[]): Command {
	let candidateCommand: PassCommand = getRandomItem(commands) as PassCommand;

	if (commands.length > 1) {
		for (const command of commands) {
			assumeType<PassCommand>(command);

			candidateCommand = getBetterPassCandidate(candidateCommand, command);
		}
	}

	return candidateCommand;
}

function getBetterPlayCandidate(previousCandidate: PlayCommand, nextCandidate: PlayCommand, gameState: GameState): PlayCommand {
	const currentRound = gameState?.rounds?.at(-1);
	const currentTrick = currentRound?.tricks?.at(-1);
	const previousCard = getCardObjectFromString(previousCandidate.data.card);
	const nextCard = getCardObjectFromString(nextCandidate.data.card);
	const winner = currentTrick ? getTrickWinner(currentTrick) : null;
	const winningCard = winner !== null && currentTrick?.cardsPlayed[winner] ? getCardObjectFromString(currentTrick?.cardsPlayed[winner]) : null;

	//idea for better bot: get rid of highest value card if it will not win the trick

	if (!currentRound || !currentTrick || !winner || !winningCard) { //If there's nothing going on yet, just play the lowest possible card

		const comparison = compareRank(previousCard.rank, nextCard.rank);

		if (comparison < 1) {

			return previousCandidate;
		}

		return nextCandidate;
	}

	const previousRankPlace = compareRank(previousCard.rank, winningCard.rank);
	const nextRankPlace = compareRank(nextCard.rank, winningCard.rank);

	if (previousRankPlace < nextRankPlace) {
		return previousCandidate;
	}

	return nextCandidate;

}

function getTrickCommand(commands: Command[], gameState: GameState): Command {
	let candidateCommand: PlayCommand = getRandomItem(commands) as PlayCommand;

	if (commands.length > 1) {
		for (const command of commands) {
			assumeType<PlayCommand>(command);

			candidateCommand = getBetterPlayCandidate(candidateCommand, command, gameState);
		}
	}

	return candidateCommand;
}

export function getLowestBotCommand(game: Manager, position: number, autoAdvanceRound = false) {
	
	const validCommands = game.getValidActions(position);

	switch (game.gameState.roundStage) {
		case RoundStage.TrickEnd:
			return makeNextStageCommand();
		case RoundStage.RoundEnd:
			if (autoAdvanceRound) {
				return makeNextStageCommand();
			}

			return getRandomItem(validCommands);
		default:

			return getRandomItem(validCommands);
		case RoundStage.Trick:
			return getTrickCommand(validCommands, game.gameState);
		case RoundStage.Pass:
			return getPassCommand(validCommands);
		case RoundStage.Deal:
			if (autoAdvanceRound) {
				return {
					command: GameAction.Deal, 
				} as Command;
			}

			break;
		case RoundStage.GameOver:
			if (autoAdvanceRound) {
				return {
					command: GameAction.NewGame, 
				} as Command;
			}

			break;
	}

	return null;
}

export class LowestBot extends BotBase {
	public getCommand(game: Manager): Command | null {
		return getLowestBotCommand(game, this.position, this.autoAdvanceRound);
	}
}
